import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FieldWrapper } from '@progress/kendo-react-form';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Error, Label } from '@progress/kendo-react-labels';
import { useEffect, useState } from 'react';
import { fetchApi } from '../../services/api';
import { Loader } from "@progress/kendo-react-indicators";
import { Input } from '@progress/kendo-react-inputs';
import { isNullOrWhiteSpaceValidator } from '../../utils/utils';
import { IDName } from '../../types/idname';
import { Button } from '@progress/kendo-react-buttons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { JsonResponse } from 'TypeGen/json-response';

type Props = {
    DriverID: string | number;
    CloseDialog: () => void;
    Refresh: () => void;
}

const hideWouldRehireReasonIds = [5]; // 5 - Deceased

const RetirePopup = (props: Props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [reasons, setReasons] = useState<IDName[]>([]);
    const [reason, setReason] = useState({ value: 0, error: '' });
    const [rehire, setRehire] = useState({ value: '', error: '' });
    const [date, setDate] = useState<Date | null>(new Date());
    const [comment, setComment] = useState('');

    const rehireOptions = [
      { ID: 'Y', Name: 'Yes' },
      { ID: 'N', Name: 'No' },
      { ID: 'U', Name: 'Under Review' }
    ]

    useEffect(() => {
        fetchApi(`/api/Asset/RetireDriver/${props.DriverID}`)
            .then((response: { Reasons: IDName[] }) => {
                setReasons(response.Reasons);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [props.DriverID]);

    const _save = () => {
        const reasonError = reason.value === 0 ? 'Please select a reason.' : '';
        const rehireError = isNullOrWhiteSpaceValidator(rehire.value, 'Please select a rehire preference.');

        if (reasonError || rehireError) {
            setReason({ ...reason, error: reasonError });
            setRehire({ ...rehire, error: rehireError });
            return;
        }
        setLoading(true);
        const data = {
            RetireReasonID: reason.value,
            WouldRehire: rehire.value,
            Comment: comment,
            RetiredDate: date
        }
        fetchApi(`/api/Asset/RetireDriver/${props.DriverID}`, data, 'POST')
            .then((response: JsonResponse) => {
                setLoading(false);
                if (response.Success) {
                  props.CloseDialog();
                  props.Refresh();
                } else {
                  alert(response.ErrorMessage);
                }
            })
            .catch(() => {
                setLoading(false);
                alert("Unable to retire");
            });
    }

    return <div className="container-fluid">
        <Dialog title="Retire Driver" onClose={props.CloseDialog} className="dialog-w525" height={460}>
            {loading && <div className="k-pos-absolute k-top-center mt-5">
                <Loader type="converging-spinner" />
            </div>}
            <div className="row">
                <div className="col">
                    <form className="k-form k-form-md p-0">
                        <FieldWrapper>
                            <Label>Reason</Label>
                            <DropDownList
                                required
                                data={reasons}
                                textField="Name"
                                dataItemKey="ID"
                                value={reasons.find(x => x.ID === reason.value)}
                                onChange={(e) => {

                                    // Reset rehire if the reason is changed to one that doesn't require it
                                    if (hideWouldRehireReasonIds.includes(reason.value)) {
                                        setRehire({ value: '', error: '' });
                                    }

                                    // Set the selected reason
                                    setReason({ value: e.target.value.ID, error: '' });

                                    // If the new reason is one that doesn't require rehire, reset it
                                    if (hideWouldRehireReasonIds.includes(e.target.value.ID)) {
                                        setRehire({ value: 'N', error: '' });
                                    }
                                }}
                            />
                            <Error>{reason.error}</Error>
                        </FieldWrapper>
                        <FieldWrapper>
                            <Label>Comment</Label>
                            <Input
                                maxLength={60}
                                value={comment}
                                onChange={(e) => setComment(e.value || '')}
                            />
                        </FieldWrapper>
                        {!hideWouldRehireReasonIds.includes(reason.value) && <FieldWrapper>
                            <Label>Would Rehire</Label>
                            <DropDownList
                                required
                                data={rehireOptions}
                                textField="Name"
                                dataItemKey="ID"
                                value={rehireOptions.find(x => x.ID === rehire.value)}
                                onChange={(e) => setRehire({ value: e.target.value.ID, error: '' })}
                            />
                            <Error>{rehire.error}</Error>
                        </FieldWrapper>}
                        <FieldWrapper>
                            <Label>Retired Date</Label>
                            <DatePicker
                                format="MM/dd/yyyy"
                                min={new Date(1753, 1, 1)}
                                max={new Date()}
                                formatPlaceholder="formatPattern"
                                value={date}
                                onChange={(e) => {
                                    setDate(e.value);
                                }}
                            />
                        </FieldWrapper>
                    </form>
                </div>
            </div>
            <DialogActionsBar>
                <Button onClick={props.CloseDialog}>Cancel</Button>
                <Button themeColor="primary" onClick={_save}>Retire Driver</Button>
            </DialogActionsBar>
        </Dialog>
    </div>
}

export default RetirePopup;
